"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";
import type { CountryCode } from "libphonenumber-js";
import { LogIn } from "lucide-react";
import { AuthShell } from "@/components/auth-shell";
import { CountryDialPicker, PhoneDialRow } from "@/components/country-dial-picker";
import { usePrefs } from "@/components/prefs-provider";
import { Button } from "@/components/ui/button";
import { Field, Input, PasswordInput } from "@/components/ui/field";
import { InlineError } from "@/components/ui/feedback";

function safeNextPath(next: string | null) {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return "/dashboard";
  if (next === "/login" || next === "/register" || next.startsWith("/api")) return "/dashboard";
  return next;
}

export default function LoginPage() {
  const { t } = usePrefs();
  const a = t.auth;
  const [country, setCountry] = useState<CountryCode>("IL");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const fd = new FormData(e.currentTarget);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: fd.get("phone"), country, password: fd.get("password") }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const m: Record<string, string> = {
          invalid_login: a.invalidLogin,
          invalid_phone: a.invalidPhone,
          phone_ambiguous: a.phoneAmbiguous,
        };
        setError(m[data.error] || a.loginFailed);
        setLoading(false);
        return;
      }
      const next = new URLSearchParams(window.location.search).get("next");
      window.location.assign(safeNextPath(next));
    } catch {
      setError(a.loginFailed);
      setLoading(false);
    }
  }

  return (
    <AuthShell
      title={a.welcome}
      lead={a.loginLead}
      footer={
        <p>
          {a.newHere}{" "}
          <Link href="/register" className="font-semibold text-brand underline-offset-2 hover:underline">
            {a.createAccount}
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={a.loginPhone} hint={a.loginPhoneHint} required>
          {(ctl) => (
            <PhoneDialRow>
              <CountryDialPicker value={country} onChange={setCountry} label={t.dash.parentCountry} />
              <Input
                {...ctl}
                name="phone"
                type="tel"
                ltr
                required
                autoFocus
                autoComplete="tel-national"
                inputMode="numeric"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="050-000-0000"
                className="min-w-0 flex-1"
              />
            </PhoneDialRow>
          )}
        </Field>
        <Field label={a.password} required>
          {(ctl) => (
            <PasswordInput
              {...ctl}
              name="password"
              autoComplete="current-password"
              required
              showLabel={t.common.showPassword}
              hideLabel={t.common.hidePassword}
            />
          )}
        </Field>
        <div className="-mt-1 text-end">
          <Link href="/forgot-password" className="text-sm font-semibold text-brand underline-offset-2 hover:underline">
            {a.forgotPassword}
          </Link>
        </div>
        <InlineError message={error} />
        <Button type="submit" size="lg" className="w-full" loading={loading} icon={<LogIn className="h-4 w-4" aria-hidden />}>
          {loading ? a.signingIn : a.login}
        </Button>
      </form>
    </AuthShell>
  );
}
